import React from 'react'
import LabeledValue from '../../../../components/common/LabeledValue'
import { TimeOffRequestDetails, isVacationBid } from '../../../../store/slices/Requestor/timeOffDetailsSlice'
import { normalizeStatus } from './constants'
import { getVacationBidAwareTableProps } from './vacationBidOverlapDisplay'
import RightPanel from './RightPanel'
import CurrentRequestSection from './CurrentRequestSection'
import HistorySection from './HistorySection'
import { isRetractionDeniedStatus, shouldRenderRequestorHistory } from '../../../../utils/statusFieldsUtils'

interface DeniedViewProps {
  data: TimeOffRequestDetails
  tableRows: any[]
  highlightedDates: string[]
  handlePrint?: () => void

}

/**
 * Renders the denied request details view for requestor status pages.
 * @param data - Denied request detail payload.
 * @param tableRows - Current request detail rows for the lower table section.
 * @param highlightedDates - Dates to emphasize in the request detail table.
 * @param handlePrint - Optional print handler for the right panel.
 * @returns The denied request detail layout.
 */
const DeniedView: React.FC<DeniedViewProps> = ({ data, tableRows, highlightedDates, handlePrint }) => {
  const ns = normalizeStatus(data.status)
  const isPartiallyDenied = ns === 'partially denied'
  const isRetractionDenied = isRetractionDeniedStatus(data.status)
  const heading = isRetractionDenied ? 'Time Off Request Retraction Denied' : 'Denied Time Off Request'
  const dateLabel = isRetractionDenied ? 'Retraction Denied On' : 'Denied On'
  const requestorHistory = data.history ?? []

  return (
    <div>
      <h1 className="h1">{heading}</h1>
      <div className="flex flex-col mobile:gap-4 tablet:gap-6 desktop:gap-8 desktop:flex-row">
        <div className="flex-1">
          {data.deniedOn && <div className="mb-4"><LabeledValue label={dateLabel} value={data.deniedOn} /></div>}
          {data.authorizedApprover && <div className="mb-4"><LabeledValue label="Authorized Approver" value={data.authorizedApprover} /></div>}
          {(data.requestRank != null || isVacationBid(data)) && <div className="mb-4"><LabeledValue label="Request Rank" value={data.requestRank == null ? 'NA' : String(data.requestRank)} /></div>}
          <div className="mb-4"><LabeledValue label="Approver Comments" value={data.approverComments || '-'} /></div>
          {data.denialReason && <div className="mb-4"><LabeledValue label="Denial Reason" value={data.denialReason} /></div>}
        </div>
        <RightPanel data={data} handlePrint={handlePrint} />
      </div>
      <div className="mt-6!"><hr className="-variation-1" /></div>
      {isPartiallyDenied && (
        <div className="w-full mt-6">
          <h2 className="h2">History</h2>
          {data.submittedOn && <div className="mb-4"><LabeledValue label="Retraction Submitted On" value={data.submittedOn} /></div>}
          {data.submittedBy && <div className="mb-4"><LabeledValue label="Submitted By" value={data.submittedBy} /></div>}
          {data.employeeRetractionComment && <div className="mb-4"><LabeledValue label="Employee Comments Regarding Retraction" value={data.employeeRetractionComment} /></div>}
          {data.deniedOn && <div className="mb-4"><LabeledValue label="Partially Denied On" value={data.deniedOn} /></div>}
          {data.originalRequestId && <div className="mb-4"><LabeledValue label="Denied Time Off Request ID" value={data.originalRequestId} /></div>}
          {data.returnedBy && <div className="mb-4"><LabeledValue label="Returned By" value={data.returnedBy} /></div>}
          {data.approverComments && <div className="mb-4"><LabeledValue label="Approver Comments" value={data.approverComments} /></div>}
        </div>
      )}
      {shouldRenderRequestorHistory(data.status, requestorHistory) && <HistorySection history={requestorHistory} />}
      <CurrentRequestSection {...getVacationBidAwareTableProps(tableRows, isVacationBid(data))} highlightedDates={highlightedDates} />
    </div>
  )
}

export default DeniedView
